import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SelectedEstimateOption } from '../../../types/estimates/SelectedEstimateOptionType';
import { describeRoomCombination } from '@/utils/estimates/getHotelRoomCombinationLabel';
import {
  formatPrice,
  roundUpPrice,
  sanitizeDeltaPrice,
} from '@/utils/circuits/prices/formatPrice';
import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import StyledTooltipContainer from '@/components/styled/StyledTooltipContainer';
import { InfoIcon } from 'lucide-react';
import EstimateBenefitImagesModal from '../EstimateBenefitImagesModal';
import { Room } from '@/utils/estimates/findAllRoomCombinations';
import { BenefitType } from '@/types/benefits/benefitTypes';
import {
  getRoomPensionOptions,
  toHotelPensionType,
} from '@/constants/hotelPension';
import type { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { useTranslation } from '@/i18n';
import { HotelRoomCategoryTranslation } from '@/hooks/useEstimateHotel';

interface EstimateHotelBenefitRoomOptionsProps {
  allRoomPrices: {
    combinationPrice: number;
    roomCombination: Room[];
  }[];
  selectedHotelCategory?: Required<BenefitFormData>['hotel']['HotelRoomCategory'][number];
  setSelectedHotelCategory: Dispatch<
    SetStateAction<
      | Required<BenefitFormData>['hotel']['HotelRoomCategory'][number]
      | undefined
    >
  >;
  selectedOptions: Record<string, SelectedEstimateOption>;
  setSelectedOptions: Dispatch<
    SetStateAction<Record<string, SelectedEstimateOption>>
  >;
  stepId: string;
  benefitId: string;
  allHotelCategories: {
    deltaPrice: number;
    category: Required<BenefitFormData>['hotel']['HotelRoomCategory'][number] & {
      translation?: HotelRoomCategoryTranslation;
    };
  }[];
  benefitDeltaPrice: number;
  selectCurrentHotel: () => void;
  arePensionsGlobal: boolean;
  standardPension: HotelPensionType;
  pensions: {
    value: HotelPensionType;
    label: string;
    price: number;
  }[];
  isStandardHotel?: boolean;
  shouldSetSelectedOptionsOnMount?: boolean;
  currency?: string;
  accessToken?: string;
  isViewMode?: boolean;
}

const EstimateHotelBenefitRoomOptions = ({
  allHotelCategories,
  allRoomPrices,
  selectedHotelCategory,
  setSelectedHotelCategory,
  setSelectedOptions,
  stepId,
  benefitId,
  selectedOptions,
  benefitDeltaPrice,
  selectCurrentHotel,
  arePensionsGlobal,
  isStandardHotel = false,
  shouldSetSelectedOptionsOnMount = true,
  standardPension,
  pensions,
  currency = 'EUR',
  accessToken,
  isViewMode = false,
}: EstimateHotelBenefitRoomOptionsProps) => {
  const { t } = useTranslation();
  const roomPensionOptions = useMemo(() => getRoomPensionOptions(t), [t]);
  const standardPensionLabel =
    roomPensionOptions.find((o) => o.value === standardPension)?.label ??
    standardPension;
  const [
    categoryToBeDisplayedInImagesModalInfo,
    setCategoryToBeDisplayedInImagesModalInfo,
  ] = useState<{
    imagesKeys: string[];
    title?: string;
    description?: string;
  } | null>(null);

  const leastExpansiveRoom = useMemo(() => allRoomPrices[0], [allRoomPrices]);

  const isSelectedHotel = useMemo(() => {
    if (Object.hasOwn(selectedOptions, stepId)) {
      return selectedOptions[stepId].benefitId === benefitId;
    }
    return isStandardHotel;
  }, [benefitId, isStandardHotel, selectedOptions, stepId]);

  const selectedPensionType = useMemo(
    () => selectedOptions[stepId]?.hotelPensionType ?? standardPension,
    [selectedOptions, standardPension, stepId],
  );

  const getRoomCombinationString = useCallback(
    (roomCombination?: Room[]) => roomCombination?.toSorted().toString(),
    [],
  );

  // The standard hotel has no entry until something non-default is picked,
  // and the entry may belong to another hotel of the same step.
  const getOwnOption = useCallback(
    (options: Record<string, SelectedEstimateOption>) =>
      options[stepId]?.benefitId === benefitId ? options[stepId] : undefined,
    [benefitId, stepId],
  );

  // Pension supplement already included in the option's deltaPrice. Derived
  // from the stored pension type so it survives reloading a saved estimate.
  const getOwnPensionPrice = useCallback(
    (options: Record<string, SelectedEstimateOption>) => {
      const ownPensionType = toHotelPensionType(
        getOwnOption(options)?.hotelPensionType,
      );
      return pensions.find(({ value }) => value === ownPensionType)?.price ?? 0;
    },
    [getOwnOption, pensions],
  );

  const selectPension = useCallback(
    (pensionType: HotelPensionType, pensionPrice: number) => {
      selectCurrentHotel();
      setSelectedOptions((prev) => {
        const own = getOwnOption(prev);
        const nextDelta = sanitizeDeltaPrice(
          (own?.deltaPrice ?? leastExpansiveRoom?.combinationPrice ?? 0) -
            getOwnPensionPrice(prev) +
            pensionPrice,
        );
        // Back to the included config on standard hotel: drop option entry
        // so the calculator does not show a ghost Hébergement line.
        if (
          isStandardHotel &&
          pensionType === standardPension &&
          nextDelta === 0
        ) {
          const { [stepId]: _, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [stepId]: {
            benefitId,
            deltaPrice: nextDelta,
            type: BenefitType.HOTEL,
            hotelPensionType: pensionType,
            hotelCategoryId: selectedHotelCategory?.id ?? undefined,
            roomCombination:
              own?.roomCombination ?? leastExpansiveRoom?.roomCombination,
          },
        };
      });
    },
    [
      benefitId,
      getOwnOption,
      getOwnPensionPrice,
      isStandardHotel,
      leastExpansiveRoom,
      selectCurrentHotel,
      selectedHotelCategory?.id,
      setSelectedOptions,
      standardPension,
      stepId,
    ],
  );

  // Persisted standard-hotel "options" with a spurious delta (combo vs included
  // price mismatch) must not inflate the calculator — default config = included.
  useEffect(() => {
    if (!isStandardHotel || !isSelectedHotel) return;
    const existing = selectedOptions[stepId];
    if (!existing || existing.benefitId !== benefitId) return;
    if (existing.deltaPrice === 0) return;

    const defaultCategoryId = allHotelCategories[0]?.category.id;
    const isDefaultCategory =
      !defaultCategoryId ||
      !existing.hotelCategoryId ||
      existing.hotelCategoryId === defaultCategoryId;
    const isDefaultPension =
      (existing.hotelPensionType ?? standardPension) === standardPension;
    const isDefaultCombo =
      !leastExpansiveRoom?.roomCombination ||
      !existing.roomCombination?.length ||
      getRoomCombinationString(existing.roomCombination) ===
        getRoomCombinationString(leastExpansiveRoom.roomCombination);

    if (isDefaultCategory && isDefaultPension && isDefaultCombo) {
      setSelectedOptions((prev) => {
        if (!prev[stepId] || prev[stepId].deltaPrice === 0) return prev;
        return {
          ...prev,
          [stepId]: { ...prev[stepId], deltaPrice: 0 },
        };
      });
    }
  }, [
    allHotelCategories,
    benefitId,
    getRoomCombinationString,
    isSelectedHotel,
    isStandardHotel,
    leastExpansiveRoom,
    selectedOptions,
    setSelectedOptions,
    standardPension,
    stepId,
  ]);

  useEffect(() => {
    if (!shouldSetSelectedOptionsOnMount) return;
    // Standard hotel default selection must not inject a ghost Hébergement
    // option (±0 / +1 € from float noise) into the calculator.
    if (isStandardHotel) return;
    if (!leastExpansiveRoom?.roomCombination) return;

    setSelectedOptions((prev) => {
      const existing = prev[stepId];
      const newDelta = sanitizeDeltaPrice(
        leastExpansiveRoom.combinationPrice + getOwnPensionPrice(prev),
      );
      const newCombinationString = getRoomCombinationString(
        leastExpansiveRoom.roomCombination,
      );
      const existingCombinationString = getRoomCombinationString(
        existing?.roomCombination,
      );

      if (
        existing?.benefitId === benefitId &&
        existing.roomCombination?.length
      ) {
        if (
          existingCombinationString === newCombinationString &&
          existing.hotelCategoryId === selectedHotelCategory?.id
        ) {
          const sanitizedExisting = sanitizeDeltaPrice(existing.deltaPrice);
          if (sanitizedExisting === newDelta) {
            if (sanitizedExisting === existing.deltaPrice) return prev;
            return {
              ...prev,
              [stepId]: { ...existing, deltaPrice: sanitizedExisting },
            };
          }
          return {
            ...prev,
            [stepId]: {
              ...existing,
              deltaPrice: newDelta,
            },
          };
        }
        const sanitized = sanitizeDeltaPrice(existing.deltaPrice);
        if (sanitized === existing.deltaPrice) return prev;
        return {
          ...prev,
          [stepId]: { ...existing, deltaPrice: sanitized },
        };
      }

      return {
        ...prev,
        [stepId]: {
          deltaPrice: newDelta,
          benefitId,
          type: BenefitType.HOTEL,
          hotelCategoryId: selectedHotelCategory?.id ?? '',
          roomCombination: leastExpansiveRoom.roomCombination,
          hotelPensionType:
            getOwnOption(prev)?.hotelPensionType ?? standardPension,
        },
      };
    });
  }, [
    benefitId,
    getOwnOption,
    getOwnPensionPrice,
    getRoomCombinationString,
    isStandardHotel,
    leastExpansiveRoom,
    selectedHotelCategory?.id,
    setSelectedOptions,
    shouldSetSelectedOptionsOnMount,
    standardPension,
    stepId,
  ]);

  const defaultSelectedRoomCombinationValue = useMemo(() => {
    if (!isSelectedHotel) return undefined;
    if (
      selectedOptions[stepId] &&
      selectedOptions[stepId].benefitId === benefitId &&
      selectedOptions[stepId].roomCombination !== undefined
    ) {
      return `${benefitId}-${selectedHotelCategory?.id}-${getRoomCombinationString(selectedOptions[stepId]?.roomCombination)}`;
    }
    return `${benefitId}-${selectedHotelCategory?.id}-${getRoomCombinationString(leastExpansiveRoom?.roomCombination)}`;
  }, [
    benefitId,
    getRoomCombinationString,
    isSelectedHotel,
    leastExpansiveRoom?.roomCombination,
    selectedHotelCategory?.id,
    selectedOptions,
    stepId,
  ]);

  return (
    <div className="flex flex-col gap-8 p-4 border border-brand-100 rounded-lg w-full">
      <div className="flex flex-col gap-4 w-full">
        <h2 className="text-sm leading-6 font-semibold text-black">
          1. {t('estimates.details.hotel.travelersDistribution')}
        </h2>
        <RadioGroup
          className="flex flex-col gap-2"
          value={defaultSelectedRoomCombinationValue}
          disabled={isViewMode}
        >
          {allRoomPrices.map(
            ({ combinationPrice, roomCombination }, indexCombination) => (
              <div
                className="flex gap-4 items-center"
                key={`room-${indexCombination}`}
              >
                <RadioGroupItem
                  value={`${benefitId}-${selectedHotelCategory?.id}-${getRoomCombinationString(roomCombination)}`}
                  disabled={combinationPrice === Infinity}
                  onClick={() => {
                    selectCurrentHotel();
                    setSelectedOptions((prev) => {
                      const nextDelta = sanitizeDeltaPrice(
                        combinationPrice + getOwnPensionPrice(prev),
                      );
                      // Default (included) room on standard hotel: drop option entry
                      // so the calculator does not show a ghost Hébergement line.
                      if (isStandardHotel && nextDelta === 0) {
                        const { [stepId]: _, ...rest } = prev;
                        return rest;
                      }
                      return {
                        ...prev,
                        [stepId]: {
                          benefitId,
                          type: BenefitType.HOTEL,
                          deltaPrice: nextDelta,
                          hotelCategoryId: selectedHotelCategory?.id,
                          roomCombination: roomCombination.toSorted(),
                          hotelPensionType:
                            getOwnOption(prev)?.hotelPensionType ??
                            standardPension,
                        } as SelectedEstimateOption,
                      };
                    });
                  }}
                  checked={
                    isSelectedHotel
                      ? roomCombination.toSorted().toString() ===
                        getRoomCombinationString(
                          selectedOptions[stepId]?.roomCombination ??
                            leastExpansiveRoom.roomCombination,
                        )
                      : false
                  }
                />
                <span className="text-sm font-normal text-gray-700">
                  {describeRoomCombination(roomCombination, t)}
                </span>
                <span className="text-sm font-semibold text-brand-700 whitespace-nowrap">
                  {indexCombination === 0
                    ? t('common.included')
                    : (() => {
                        const diff =
                          combinationPrice -
                          roundUpPrice(leastExpansiveRoom.combinationPrice);
                        const sign = diff > 0 ? '+' : '';
                        return `${sign} ${formatPrice(diff)} ${formatCurrency(currency)}`;
                      })()}
                </span>
              </div>
            ),
          )}
        </RadioGroup>
      </div>
      <div className="flex flex-col gap-4 w-full">
        <h2 className="text-sm leading-6 font-semibold text-black">
          2. {t('estimates.details.hotel.accommodationCategory')}
        </h2>
        <RadioGroup
          className="flex flex-col gap-2"
          value={
            shouldSetSelectedOptionsOnMount
              ? (selectedHotelCategory?.id ?? '')
              : undefined
          }
          disabled={isViewMode}
        >
          {allHotelCategories.map(({ category, deltaPrice }, indexCategory) => (
            <div
              className="flex gap-4 items-center"
              key={`category-${indexCategory}`}
            >
              <RadioGroupItem
                disabled={deltaPrice === Infinity}
                value={category.id ?? deltaPrice.toString()}
                onClick={() => {
                  selectCurrentHotel();
                  setSelectedHotelCategory(category);
                  // Without common pension prices, the pension resets with the category.
                  setSelectedOptions((prev) => ({
                    ...prev,
                    [stepId]: {
                      benefitId,
                      deltaPrice: sanitizeDeltaPrice(
                        deltaPrice +
                          (arePensionsGlobal ? getOwnPensionPrice(prev) : 0),
                      ),
                      type: BenefitType.HOTEL,
                      hotelCategoryId: category.id ?? undefined,
                      hotelPensionType: arePensionsGlobal
                        ? (getOwnOption(prev)?.hotelPensionType ??
                          standardPension)
                        : standardPension,
                    },
                  }));
                }}
                checked={
                  isSelectedHotel
                    ? category.id === selectedHotelCategory?.id
                    : false
                }
              />
              <span className="text-sm font-normal text-gray-700">
                {category.translation?.name ?? category.name}
              </span>
              <span className="text-sm font-semibold text-brand-700 whitespace-nowrap">
                {indexCategory === 0
                  ? t('common.included')
                  : (() => {
                      const diff = deltaPrice - roundUpPrice(benefitDeltaPrice);
                      const sign = diff > 0 ? '+' : '';
                      return `${sign} ${formatPrice(diff)} ${formatCurrency(currency)}`;
                    })()}
              </span>
              <StyledTooltipContainer tooltipMessage="Afficher les détails de cette catégorie">
                <InfoIcon
                  className="size-5 fill-gray-400 stroke-white hover:cursor-pointer"
                  onClick={() => {
                    if (category.id) {
                      setCategoryToBeDisplayedInImagesModalInfo({
                        imagesKeys: category.mainImage
                          ? [category.mainImage].concat(category.images ?? [])
                          : (category.images ?? []),
                        title:
                          category.translation?.name ??
                          category.name ??
                          undefined,
                        description:
                          category.translation?.longDescription ??
                          category.longDescription ??
                          undefined,
                      });
                    }
                  }}
                />
              </StyledTooltipContainer>
            </div>
          ))}
        </RadioGroup>
      </div>
      <div className="flex flex-col gap-4 w-full">
        <h2 className="text-sm leading-6 font-semibold text-black">
          3. {t('estimates.details.hotel.pensionType')}
        </h2>
        <RadioGroup
          className="flex flex-col gap-2"
          value={isSelectedHotel ? selectedPensionType : undefined}
        >
          <div className="flex gap-4 items-center">
            <RadioGroupItem
              value={standardPension}
              onClick={() => selectPension(standardPension, 0)}
              checked={
                isSelectedHotel
                  ? standardPension === selectedPensionType
                  : false
              }
            />
            <span className="text-sm font-normal text-gray-700">
              {standardPensionLabel}
            </span>
            <span className="text-sm font-semibold text-brand-700 whitespace-nowrap">
              {t('common.included')}
            </span>
          </div>
          {pensions.map(({ value, label, price }, indexPension) => (
            <div
              className="flex gap-4 items-center"
              key={`category-${indexPension}`}
            >
              <RadioGroupItem
                disabled={price === Infinity}
                value={value}
                onClick={() => selectPension(value, price)}
                checked={
                  isSelectedHotel ? value === selectedPensionType : false
                }
              />
              <span className="text-sm font-normal text-gray-700">{label}</span>
              <span className="text-sm font-semibold text-brand-700 whitespace-nowrap">
                {(() => {
                  const sign = price > 0 ? '+' : '';
                  return `${sign} ${formatPrice(price)} ${formatCurrency(currency)}`;
                })()}
              </span>
            </div>
          ))}
        </RadioGroup>
      </div>
      {categoryToBeDisplayedInImagesModalInfo !== null && (
        <EstimateBenefitImagesModal
          closeModal={() => {
            setCategoryToBeDisplayedInImagesModalInfo(null);
          }}
          imageKeys={categoryToBeDisplayedInImagesModalInfo.imagesKeys}
          title={categoryToBeDisplayedInImagesModalInfo.title}
          description={categoryToBeDisplayedInImagesModalInfo.description}
          accessToken={accessToken}
        />
      )}
    </div>
  );
};

export default EstimateHotelBenefitRoomOptions;
